import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

/**
 * Club roles (klubbadmin + skyteleder) managed by the club's own admins.
 * Backend: GET /api/users/club-roles/?customer=<id>, PATCH /api/users/club-roles/:id.
 */
class ClubRoleStore extends LocalModel {
    constructor() {
        super('clubRole', {
            namePlural: 'clubRoles',
            namePluralReal: 'club-roles',
            sort: 'firstname',
            limit: 500,
            api: {
                search: { url: '/api/users/club-roles/', params: {} },
                load: { url: '/api/users/club-roles/', params: {} },
                save: { url: '/api/users/club-roles/', params: {} },
                delete: { url: '/api/users/club-roles/', params: {} },
            },
        });
    }

    @observable clubRoles = [];

    @observable isSaving = {};

    @observable searchResult = [];

    @observable isSearching = false;

    @observable lastError = '';

    @action
    async loadRoles(customer) {
        this.updateKeyValue('isLoading', true);
        const response = await util.fetchApi('/api/users/club-roles/', { method: 'GET' }, { customer });
        if (response?.status === 200) {
            this.updateKeyValue('clubRoles', response.data || []);
        }
        this.updateKeyValue('isLoading', false);
        return response;
    }

    /** Grant (value 1) or revoke (value 0) a role; the backend applies the club-role rules. */
    @action
    async setRole({ userId, role, value, customer }) {
        this.updateKeyValue('isSaving', { ...this.isSaving, [userId]: true });
        this.updateKeyValue('lastError', '');
        const response = await util.fetchApi(`/api/users/club-roles/${userId}`, { method: 'PATCH' }, { role, value, customer });
        if (response?.status === 200 && response.data?.user) {
            const updated = response.data.user;
            const exists = this.clubRoles.some(u => u.id === updated.id);
            const next = exists
                ? this.clubRoles.map(u => (u.id === updated.id ? { ...u, ...updated } : u))
                : [...this.clubRoles, updated];
            this.updateKeyValue('clubRoles', next.filter(u => u.isCustomerAdmin || u.isRangeOfficer));
        } else {
            this.updateKeyValue('lastError', response?.message || 'Could not update the role.');
        }
        const { [userId]: done, ...rest } = this.isSaving;
        this.updateKeyValue('isSaving', rest);
        return response;
    }

    /** Member picker: platform-wide user search (same endpoint as the other staff pickers). */
    @action
    async searchUsers(search) {
        if (!search || search.trim().length < 2) {
            this.updateKeyValue('searchResult', []);
            return null;
        }
        this.updateKeyValue('isSearching', true);
        const response = await util.fetchApi('/api/users/search-all', { method: 'GET' }, { search: search.trim() });
        if (response?.status === 200) {
            this.updateKeyValue('searchResult', response.data || []);
        }
        this.updateKeyValue('isSearching', false);
        return response;
    }

    @action
    clearSearch() {
        this.updateKeyValue('searchResult', []);
    }
}

const store = new ClubRoleStore();
export default store;
